import { Alert, Button } from "@opowiedzo/ui";
import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useInvalidate,
  useList,
  useOne,
} from "@refinedev/core";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/Badge";
import { Labeled, SelectInput, TextArea, TextInput } from "@/components/Form";
import {
  BackLink,
  Card,
  CardNote,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import { channelLabel, deliveryStatus, formatDateTime, plural } from "@/lib/format";
import type { MemoirRow } from "@/pages/memoirs/list";
import type { MemoirDetail } from "@/pages/memoirs/show";
import type { SystemState } from "@/pages/system";
import type { AdminIdentity } from "@/providers/authProvider";
import type { DeliveryRow } from "./deliveries";
import type { TemplateRow } from "./templates";

type Channel = "email" | "sms" | "phone";

/** Ile pozycji ściągamy do list wyboru. Reszty szuka się wyszukiwarką. */
const MEMOIR_LIMIT = 50;
const TEMPLATE_LIMIT = 100;
/** Backend przycina własną treść pytania na 500 znakach. */
const TEXT_LIMIT = 500;

interface ChannelInfo {
  value: Channel;
  label: string;
  hint: string;
  /** Klucz integracji z ekranu System - bez niego wysyłka idzie na konsolę. */
  integration: string;
  offline: string;
}

const CHANNELS: ChannelInfo[] = [
  {
    value: "email",
    label: "E-mail",
    hint: "Pytanie z linkiem do strony odpowiedzi.",
    integration: "smtp",
    offline:
      "Poczta wychodząca nie ma konfiguracji - wiadomość wyląduje w logu serwera, nie w skrzynce narratora.",
  },
  {
    value: "sms",
    label: "SMS",
    hint: "Krótka treść i skrócony link. Polskie znaki tną wiadomość do 70 znaków na część.",
    integration: "smsapi",
    offline: "SMSAPI nie ma tokenu - SMS wyląduje w logu serwera, nie na telefonie narratora.",
  },
  {
    value: "phone",
    label: "Telefon",
    hint: "Agent głosowy dzwoni, czyta pytanie i słucha odpowiedzi.",
    integration: "elevenlabs",
    offline:
      "ElevenLabs nie ma kompletu kluczy - rozmowa wyląduje w logu serwera i nikt nigdzie nie zadzwoni.",
  },
];

/**
 * Wysyłka pytania na żądanie. Ekran istnieje po to, żeby przed startem
 * sprzedaży sprawdzić każdy z trzech kanałów na żywym narratorze - stąd
 * wybór osoby, pytania i kanału obok siebie, a wynik wprost pod przyciskiem.
 */
export function SendPromptPage() {
  const [searchParams] = useSearchParams();
  const { data: identity, isLoading: tozsamosc } = useGetIdentity<AdminIdentity | null>();
  const uprawnienia = identity?.permissions ?? [];
  const mozeDzialac = uprawnienia.includes("deliveries.act");

  const [memoirId, setMemoirId] = useState(searchParams.get("memoir_id") ?? "");
  const [szukajOpowiesci, setSzukajOpowiesci] = useState("");
  const [zrodlo, setZrodlo] = useState<"library" | "custom">("library");
  const [szukajPytania, setSzukajPytania] = useState("");
  const [pytanie, setPytanie] = useState<TemplateRow | null>(null);
  const [tresc, setTresc] = useState("");
  const [kanal, setKanal] = useState<Channel | "">("");
  const [powod, setPowod] = useState("");
  const [wynik, setWynik] = useState<DeliveryRow | null>(null);

  const invalidate = useInvalidate();
  const { mutateAsync, mutation } = useCustomMutation<DeliveryRow>();

  const opowiesci = useList<MemoirRow>({
    resource: "memoirs",
    pagination: { currentPage: 1, pageSize: MEMOIR_LIMIT },
    sorters: [{ field: "title", order: "asc" }],
    filters: [{ field: "query", operator: "eq", value: szukajOpowiesci }],
    queryOptions: { enabled: mozeDzialac },
  });

  const pytania = useList<TemplateRow>({
    resource: "prompt-templates",
    pagination: { currentPage: 1, pageSize: TEMPLATE_LIMIT },
    sorters: [{ field: "category", order: "asc" }],
    filters: [
      { field: "query", operator: "eq", value: szukajPytania },
      { field: "active", operator: "eq", value: "true" },
    ],
    queryOptions: { enabled: mozeDzialac && zrodlo === "library" },
  });

  // Dane kontaktowe narratora są tylko na detalu, a bez nich nie da się
  // powiedzieć, który kanał w ogóle ma szansę zadziałać.
  const { query: detal } = useOne<MemoirDetail>({
    resource: "memoirs",
    id: memoirId,
    queryOptions: { enabled: mozeDzialac && memoirId !== "" },
  });
  const opowiesc = detal.data?.data;

  // Stan integracji tłumaczy najgorszy możliwy wynik testu: status "wysłane"
  // przy wiadomości, która poszła na konsolę serwera.
  const { query: system } = useCustom<SystemState>({
    url: "/system",
    method: "get",
    queryOptions: { enabled: mozeDzialac && uprawnienia.includes("system.read") },
  });
  const integracje = system.data?.data.integrations;

  const wybranyKanal = CHANNELS.find((pozycja) => pozycja.value === kanal);
  const integracjaKanalu = integracje?.find((pozycja) => pozycja.key === wybranyKanal?.integration);

  const zmienOpowiesc = (value: string) => {
    setMemoirId(value);
    // Kanał wykonalny dla jednej osoby nie musi być wykonalny dla następnej.
    setKanal("");
    setWynik(null);
  };

  const opcjeOpowiesci = opowiesci.result.data.map((memoir) => ({
    value: memoir.id,
    label: `${memoir.storyteller_name ?? "bez narratora"} - ${memoir.title}`,
  }));
  // Opowieść z adresu albo wybrana przed zawężeniem wyszukiwania musi zostać
  // na liście, inaczej pole pokazuje pustkę mimo dokonanego wyboru.
  if (memoirId && opowiesc && !opcjeOpowiesci.some((opcja) => opcja.value === memoirId)) {
    opcjeOpowiesci.unshift({
      value: memoirId,
      label: `${opowiesc.storyteller_name ?? "bez narratora"} - ${opowiesc.title}`,
    });
  }

  const opcjePytan = pytania.result.data.map((template) => ({
    value: template.id,
    label: `${template.category_name}: ${template.text}`,
  }));
  if (pytanie && !opcjePytan.some((opcja) => opcja.value === pytanie.id)) {
    opcjePytan.unshift({
      value: pytanie.id,
      label: `${pytanie.category_name}: ${pytanie.text}`,
    });
  }

  const trescGotowa = zrodlo === "library" ? pytanie !== null : tresc.trim().length > 0;
  const kanalGotowy = kanal !== "" && przeszkoda(kanal, opowiesc) === null;
  const gotowy =
    mozeDzialac &&
    memoirId !== "" &&
    trescGotowa &&
    kanalGotowy &&
    powod.trim().length >= 3 &&
    !mutation.isPending;

  const wyslij = () => {
    setWynik(null);
    mutateAsync({
      url: "/prompt-deliveries/send-now",
      method: "post",
      values: {
        memoir_id: memoirId,
        channel: kanal,
        prompt_id: zrodlo === "library" ? (pytanie?.id ?? null) : null,
        text: zrodlo === "custom" ? tresc.trim() : null,
        reason: powod.trim(),
      },
      successNotification: false,
      errorNotification: false,
    })
      .then((odpowiedz) => setWynik(odpowiedz.data))
      .catch(() => {
        // Treść błędu pokazuje mutation.error. Przy 502 wysyłka i tak leży
        // w bazie ze statusem "błąd", więc lista musi się przeładować.
      })
      .finally(() => invalidate({ resource: "prompt-deliveries", invalidates: ["list"] }));
  };

  return (
    <>
      <BackLink to="/wysylki-pytan">Wysyłki pytań</BackLink>
      <PageHeader
        title="Wyślij pytanie teraz"
        description="Wysyłka poza harmonogramem, prosto do narratora. Zakłada nową pozycję w logu wysyłek i idzie od razu - po to, żeby sprawdzić, czy kanał naprawdę dochodzi."
      />

      {tozsamosc ? (
        <Loading />
      ) : !mozeDzialac ? (
        <Card>
          <p className="text-base text-ink-soft">
            Twoja rola nie obejmuje wysyłania pytań. Poproś operacje i podaj powód - trafi do audytu
            razem z wysyłką.
          </p>
        </Card>
      ) : (
        <DetailGrid
          main={
            <>
              <Card title="Do kogo">
                <div className="flex flex-col gap-3">
                  <Labeled
                    label="Szukaj Opowieści"
                    hint="Szuka po imieniu narratora, tytule i adresie e-mail."
                  >
                    <TextInput
                      type="search"
                      value={szukajOpowiesci}
                      placeholder="np. Maria"
                      onChange={(event) => setSzukajOpowiesci(event.target.value)}
                    />
                  </Labeled>

                  <Labeled
                    label="Opowieść"
                    hint={podsumowanieListy(
                      opowiesci.result.total ?? 0,
                      opcjeOpowiesci.length,
                      "Opowieść",
                      "Opowieści",
                      "Opowieści",
                    )}
                  >
                    <SelectInput
                      value={memoirId}
                      onChange={(event) => zmienOpowiesc(event.target.value)}
                    >
                      <option value="">-- wybierz --</option>
                      {opcjeOpowiesci.map((opcja) => (
                        <option key={opcja.value} value={opcja.value}>
                          {opcja.label}
                        </option>
                      ))}
                    </SelectInput>
                  </Labeled>

                  <ErrorBox error={opowiesci.query.error} />

                  {opowiesc && (
                    <DefinitionList
                      rows={[
                        ["Narrator", opowiesc.storyteller_name ?? "-"],
                        ["E-mail", opowiesc.storyteller_email ?? "-"],
                        ["Telefon", opowiesc.storyteller_phone ?? "-"],
                      ]}
                    />
                  )}
                </div>
              </Card>

              <Card title="Jakie pytanie">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap gap-4 text-base text-ink">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="zrodlo"
                        className="accent-accent"
                        checked={zrodlo === "library"}
                        onChange={() => {
                          setZrodlo("library");
                          setTresc("");
                        }}
                      />
                      Pytanie z bazy
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="zrodlo"
                        className="accent-accent"
                        checked={zrodlo === "custom"}
                        onChange={() => {
                          setZrodlo("custom");
                          setPytanie(null);
                        }}
                      />
                      Własna treść
                    </label>
                  </div>

                  {zrodlo === "library" ? (
                    <>
                      <Labeled label="Szukaj w treści pytań">
                        <TextInput
                          type="search"
                          value={szukajPytania}
                          placeholder="np. wakacje"
                          onChange={(event) => setSzukajPytania(event.target.value)}
                        />
                      </Labeled>

                      <Labeled
                        label="Pytanie"
                        hint={podsumowanieListy(
                          pytania.result.total ?? 0,
                          opcjePytan.length,
                          "włączone pytanie",
                          "włączone pytania",
                          "włączonych pytań",
                        )}
                      >
                        <SelectInput
                          value={pytanie?.id ?? ""}
                          onChange={(event) => {
                            const wybrane = pytania.result.data.find(
                              (template) => template.id === event.target.value,
                            );
                            setPytanie(wybrane ?? null);
                          }}
                        >
                          <option value="">-- wybierz --</option>
                          {opcjePytan.map((opcja) => (
                            <option key={opcja.value} value={opcja.value}>
                              {opcja.label}
                            </option>
                          ))}
                        </SelectInput>
                      </Labeled>

                      <ErrorBox error={pytania.query.error} />

                      {pytanie && (
                        <div className="rounded-card border border-line bg-bg px-3.5 py-3">
                          <p className="text-xs text-ink-soft">{pytanie.category_name}</p>
                          <p className="mt-1 text-lg text-ink">{pytanie.text}</p>
                        </div>
                      )}
                      <CardNote>
                        Narrator zobaczy wariant dopasowany do rodzaju i formy zwracania się - treść
                        powyżej jest wersją bazową.
                      </CardNote>
                    </>
                  ) : (
                    <>
                      <Labeled
                        label="Własna treść pytania"
                        hint={`${tresc.length} z ${TEXT_LIMIT} znaków`}
                      >
                        <TextArea
                          rows={3}
                          maxLength={TEXT_LIMIT}
                          value={tresc}
                          placeholder="np. Jak wyglądał Wasz pierwszy wspólny dom?"
                          onChange={(event) => setTresc(event.target.value)}
                        />
                      </Labeled>
                      <CardNote>
                        Własna treść idzie do narratora dokładnie tak, jak ją wpiszesz - bez
                        wariantów gramatycznych z bazy.
                      </CardNote>
                    </>
                  )}
                </div>
              </Card>

              <Card title="Którym kanałem">
                <fieldset className="flex flex-col gap-2">
                  <legend className="sr-only">Kanał wysyłki</legend>
                  {CHANNELS.map((pozycja) => {
                    const blokada = przeszkoda(pozycja.value, opowiesc, detal.isLoading);
                    const wybrany = kanal === pozycja.value;
                    return (
                      <label
                        key={pozycja.value}
                        className={`flex items-start gap-2.5 rounded-card border px-3.5 py-2.75 ${
                          blokada
                            ? "cursor-not-allowed border-line opacity-60"
                            : wybrany
                              ? "border-accent bg-accent/5"
                              : "border-line hover:border-ink-soft"
                        }`}
                      >
                        <input
                          type="radio"
                          name="kanal"
                          className="mt-1 accent-accent"
                          checked={wybrany}
                          disabled={blokada !== null}
                          onChange={() => setKanal(pozycja.value)}
                        />
                        <span>
                          <span className="block text-base text-ink">{pozycja.label}</span>
                          <span className="block text-xs text-ink-soft">
                            {blokada ?? pozycja.hint}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </fieldset>

                {wybranyKanal && integracjaKanalu && !integracjaKanalu.configured && (
                  <div className="mt-3.5">
                    <Alert tone="note">{wybranyKanal.offline}</Alert>
                  </div>
                )}

                {kanal === "phone" && (
                  <CardNote>
                    Przy telefonie "wysłane" znaczy tylko tyle, że zleciliśmy połączenie. Rozmowa
                    dopiero się odbędzie, a jej treść pojawi się później jako historia - nie czekaj
                    na nią na tym ekranie.
                  </CardNote>
                )}
              </Card>
            </>
          }
          side={
            <>
              <Card title="Wysyłka">
                <div className="flex flex-col gap-3">
                  <Labeled label="Powód (trafi do audytu)">
                    <TextArea
                      rows={2}
                      value={powod}
                      placeholder="np. sprawdzenie kanału SMS przed startem sprzedaży"
                      onChange={(event) => setPowod(event.target.value)}
                    />
                  </Labeled>

                  {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}
                  {mutation.error?.statusCode === 502 && (
                    <CardNote>
                      Dostawca odmówił, ale wysyłka została zapisana w logu ze statusem "Błąd
                      wysyłki" razem z powodem.{" "}
                      <Link
                        to={`/wysylki-pytan?memoir_id=${memoirId}&failures_only=true`}
                        className="text-accent underline underline-offset-3 hover:text-accent-dark"
                      >
                        Zobacz błędy tej Opowieści
                      </Link>
                      .
                    </CardNote>
                  )}

                  <Button variant="primary" disabled={!gotowy} onClick={wyslij}>
                    {mutation.isPending ? "Wysyłam..." : "Wyślij pytanie teraz"}
                  </Button>

                  <CardNote>
                    Wysyłka idzie od razu, bez kolejki - odpowiedź poniżej jest tym, co naprawdę
                    zrobił dostawca. Powód, Twoje konto i wybrany kanał zostają w audycie.
                  </CardNote>
                </div>
              </Card>

              {wynik && <ResultCard delivery={wynik} />}
            </>
          }
        />
      )}
    </>
  );
}

/**
 * Dlaczego tym kanałem się nie da. Wyszarzony wybór z wyjaśnieniem jest
 * uczciwszy niż klikalny przycisk kończący się błędem 422 z serwera.
 */
function przeszkoda(
  channel: Channel,
  memoir: MemoirDetail | undefined,
  wczytywanie = false,
): string | null {
  if (!memoir) {
    return wczytywanie ? "Sprawdzam dane kontaktowe narratora..." : "Najpierw wybierz Opowieść.";
  }
  if (!memoir.storyteller_name && !memoir.storyteller_email && !memoir.storyteller_phone) {
    return "Ta Opowieść nie ma jeszcze narratora.";
  }
  if (channel === "email" && !memoir.storyteller_email) return "Narrator nie ma adresu e-mail.";
  if (channel !== "email" && !memoir.storyteller_phone) return "Narrator nie ma numeru telefonu.";
  return null;
}

/** Ile pozycji pasuje i ile z nich widać - lista ucięta w ciszy kłamie. */
function podsumowanieListy(
  total: number,
  widoczne: number,
  one: string,
  few: string,
  many: string,
): string {
  const opis = `${total} ${plural(total, one, few, many)}`;
  return total > widoczne
    ? `${opis} - lista pokazuje pierwsze ${widoczne}, zawęź wyszukiwaniem.`
    : opis;
}

/**
 * Co się stało z wysyłką. To jest cały sens tego ekranu, więc status, godzina
 * i powód błędu stoją obok siebie, a nie po kliknięciu w log.
 */
function ResultCard({ delivery }: { delivery: DeliveryRow }) {
  const status = deliveryStatus(delivery.status);

  return (
    <Card title="Co się stało">
      <DefinitionList
        rows={[
          [
            "Status",
            <Badge key="status" tone={status.tone}>
              {status.label}
            </Badge>,
          ],
          ["Kanał", channelLabel(delivery.channel)],
          ["Wysłane", formatDateTime(delivery.sent_at)],
          ["Powód błędu", delivery.failed_reason ?? "-"],
        ]}
      />

      {delivery.question && <p className="mt-3.5 text-base text-ink">{delivery.question}</p>}

      <p className="mt-3.5">
        <Link
          to={`/wysylki-pytan/${delivery.id}`}
          className="text-accent underline underline-offset-4 hover:text-accent-dark"
        >
          Otwórz tę wysyłkę w logu
        </Link>
      </p>

      <CardNote>
        {delivery.channel === "phone"
          ? "Połączenie jest zlecone. Odebrana rozmowa wróci sama - dopiero wtedy pojawi się historia."
          : "Status pochodzi od dostawcy w chwili wysyłki. Doręczenia i otwarcia dopisują się później."}
      </CardNote>
    </Card>
  );
}
